/**
 * MBA Lab — GA4 analytics helper.
 *
 * Use trackEvent / trackConversion for all client-side events. Every event must be declared in
 * src/analytics-events.js; src/analytics-guard.js strips anything undeclared before it reaches
 * gtag. No PII, ever - see docs/TOOLS.md.
 *
 * Two properties worth knowing before changing anything here:
 *
 *  1. Nothing in this file may throw into a caller. Analytics is wrapped around real user
 *     interactions (tool buttons, link clicks); a throw here would abort the interaction itself
 *     rather than just losing a metric. Every public entry point goes through safe().
 *
 *  2. This module drains the queue that Views/Shared/_AnalyticsHead.cshtml installs. That queue
 *     exists because layout.bundle.js is deferred and therefore runs AFTER parsing, while callers
 *     outside the bundle run during it. Views/Home/ComingSoon.cshtml has an inline
 *     `if (window.MbaLabAnalytics)` guard that, before the queue existed, had never once been true
 *     in any environment - silently, with no error and no failing test.
 */

import { SCHEMA_VERSION } from './analytics-events.js';
import { sanitizeParams } from './analytics-guard.js';
import * as buckets from './analytics-buckets.js';

/**
 * Hard ceiling per page load. A runaway listener - a click handler bound twice, an observer in a
 * loop - would otherwise fire thousands of events, wreck the reports, and burn quota. This is the
 * client-side half of the kill switch; GoogleAnalytics:Enabled is the server-side half.
 */
export const MAX_EVENTS_PER_PAGE = 150;

let eventsSent = 0;
let capWarned = false;

function isDevelopment() {
  // webpack substitutes this at build time from `mode`. Undefined under `node --test`, which is
  // deliberately treated as development so unit tests see the loud path.
  return typeof process === 'undefined' || !process.env || process.env.NODE_ENV !== 'production';
}

/** Server-rendered config from _AnalyticsHead.cshtml. Null if the partial never ran. */
function serverConfig() {
  return (typeof window !== 'undefined' && window.__mbalabAnalytics) || null;
}

/**
 * Whether analytics is switched on at all. Read this BEFORE attaching any listener or observer -
 * that is the difference between a kill switch that stops data and one that stops behaviour.
 */
export function analyticsEnabled() {
  const config = serverConfig();
  return !!(config && config.enabled === true);
}

/**
 * Whether a specific subsystem is switched on, so one misbehaving listener can be disabled without
 * taking the rest down.
 * @param {string} name - key from GoogleAnalyticsOptions.Modules, camelCased.
 */
export function moduleEnabled(name) {
  const config = serverConfig();
  if (!config || config.enabled !== true) return false;
  if (!config.modules) return true;
  return config.modules[name] !== false;
}

function getGtag() {
  return typeof window !== 'undefined' && typeof window.gtag === 'function' ? window.gtag : null;
}

/**
 * Params merged into every event.
 *
 * Deliberately does NOT send page_path or referrer. Both are Universal Analytics names: GA4 derives
 * the path from page_location and names the referrer page_referrer, so ours arrived as unregistered
 * params and were dropped from every report. Worse, a full document.referrer can carry another
 * site's query string, which we were attaching to every event from all eleven tools.
 */
function baseParams() {
  const params = { schema_version: SCHEMA_VERSION };

  if (typeof document !== 'undefined' && document.title) {
    params.page_title = document.title;
  }

  const config = serverConfig();
  if (config && config.pageType) {
    params.page_type = config.pageType;
  }

  return params;
}

function reportProblems(problems) {
  if (!problems.length || !isDevelopment()) return;
  // Development only, and never a throw: this runs inside real click handlers, so raising here
  // would break the user's interaction rather than just the telemetry. The node tests in
  // src/tests/analytics.test.js are what enforce this contract mechanically.
  problems.forEach((problem) => {
    // eslint-disable-next-line no-console
    console.error('[MbaLabAnalytics] ' + problem);
  });
}

/** Wraps a public entry point so analytics can never take down its caller. */
function safe(fn) {
  return function safelyInvoked() {
    try {
      return fn.apply(null, arguments);
    } catch (error) {
      if (isDevelopment()) {
        // eslint-disable-next-line no-console
        console.error('[MbaLabAnalytics] threw and was suppressed:', error);
      }
      return undefined;
    }
  };
}

/**
 * Send a custom event to GA4.
 * @param {string} eventName - must be declared in src/analytics-events.js.
 * @param {Object} [params] - only params declared for that event survive. Never PII.
 */
export const trackEvent = safe(function trackEvent(eventName, params) {
  // Sanitize before the gtag check, not after: analytics is switched off in local development, so
  // this is the only place a developer can find out that an event name or param is wrong.
  const result = sanitizeParams(eventName, Object.assign(baseParams(), params || {}));
  reportProblems(result.problems);

  if (!result.registered) return;

  const gtag = getGtag();
  if (!gtag) return;

  if (eventsSent >= MAX_EVENTS_PER_PAGE) {
    if (!capWarned) {
      capWarned = true;
      gtag('event', 'analytics_cap_reached', { schema_version: SCHEMA_VERSION });
    }
    return;
  }

  eventsSent += 1;
  gtag('event', eventName, result.params);
});

/**
 * Track a conversion-style event. Identical to trackEvent - "conversion" is a designation made in
 * the GA4 UI (Admin -> Events -> Mark as key event), and nothing in this code can enforce it.
 */
export const trackConversion = safe(function trackConversion(eventName, params) {
  trackEvent(eventName, params);
});

/**
 * Explicit page view.
 *
 * WARNING: there is no legitimate caller for this today, and adding one would double-count. GA4
 * already sends page_view on load (send_page_view: true), and the hub navigates with real page
 * loads rather than the History API, so there are currently zero virtual views on this site.
 * Reserved for genuine SPA-style navigation, if that ever exists.
 */
export const trackPageView = safe(function trackPageView(path, title, pageType) {
  const params = {};
  if (path) params.page_path = path;
  if (title) params.page_title = title;
  if (pageType) params.page_type = pageType;

  // Routed through trackEvent rather than calling gtag directly, so it goes through the same
  // registry and PII guard as everything else. A direct call here would be a hole in the guard.
  trackEvent('page_view', params);
});

const api = {
  trackEvent,
  trackConversion,
  trackPageView,
  analyticsEnabled,
  moduleEnabled,
  // Exposed for wwwroot/js/college-search.js, which is outside webpack and cannot import. Sharing
  // the implementation rather than copying it keeps one definition of every bucket boundary.
  buckets,
};

// Replace the head-installed queue shim with the real implementation, then flush whatever was
// queued while the deferred bundle was still loading. Guarded against a double evaluation of this
// module draining the same queue twice.
if (typeof window !== 'undefined') {
  const pending = window.MbaLabAnalytics && Array.isArray(window.MbaLabAnalytics._q)
    ? window.MbaLabAnalytics._q.splice(0)
    : [];

  window.MbaLabAnalytics = api;

  pending.forEach(function drain(entry) {
    const method = entry[0];
    const args = entry[1];
    if (typeof api[method] === 'function') {
      api[method].apply(null, args);
    }
  });
}
